import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  TextField,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  Refresh,
  Key,
  PersonAdd,
  Block,
  CheckCircle,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ContentCopy,
  Casino,
  Edit,
  Business,
  RestartAlt,
  UnfoldMore,
  UnfoldLess,
  Email,
  Download,
} from '@mui/icons-material';
import {
  CompanyAccount,
  MemberAccount,
  getCompanyAccounts,
  createCompanyAccount,
  addEmailAccount,
  setAccountPassword,
  setAccountEmail,
  setAccountDisabled,
  setDefaultPasswords,
} from '../lib/api';
import { FilterBar } from '../components/common';

// Same alphabet as the backend: no look-alike characters, easy to read out.
const PASSWORD_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
const generatePassword = (length = 10) => {
  const bytes = crypto.getRandomValues(new Uint32Array(length));
  return Array.from(bytes, b => PASSWORD_ALPHABET[b % PASSWORD_ALPHABET.length]).join('');
};

type StatusFilter = 'all' | 'active' | 'disabled' | 'none';
type MustChangeFilter = 'all' | 'yes' | 'no';
type SortKey = 'pcu_id' | 'name' | 'city' | 'status' | 'mustChange' | 'emails' | 'lastSignIn';

// Must match DEFAULT_PASSWORD_SUFFIX in backend/src/routes/accounts.ts
const DEFAULT_PASSWORD_SUFFIX = '_123';
const defaultPassword = (pcuId: string) => `${pcuId}${DEFAULT_PASSWORD_SUFFIX}`;
/** Wrap a value in an LTR isolate so Arabic (RTL) text around it can't
 *  reorder it: without this "688_123" renders as "123_688". */
const ltr = (v: string) => `\u2066${v}\u2069`;
const isCompanyLogin = (email: string) => email.endsWith('@members.pcu.ps');

/** True when the company account or any email account still has an admin-set password. */
const needsChange = (r: CompanyAccount) =>
  !!r.account?.must_change_password || r.users.some(u => u.must_change_password);

/** What the account dialog is doing. */
type DialogMode =
  | { kind: 'createCompany'; company: CompanyAccount }
  | { kind: 'addEmail'; company: CompanyAccount }
  | { kind: 'password'; company: CompanyAccount; user: MemberAccount }
  | { kind: 'email'; company: CompanyAccount; user: MemberAccount };

const needsPassword = (m: DialogMode) => m.kind !== 'email';
const needsEmail = (m: DialogMode) => m.kind === 'addEmail' || m.kind === 'email';

const AuthSystemPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [rows, setRows] = useState<CompanyAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [mustChangeFilter, setMustChangeFilter] = useState<MustChangeFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ ok: number; created: number; failed: string[] } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // all companies start expanded (email accounts visible); track the closed ones
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('pcu_id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [snackbar, setSnackbar] = useState('');

  // Account dialog (create / add email account / reset password / change email)
  const [mode, setMode] = useState<DialogMode | null>(null);
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [dialogError, setDialogError] = useState('');

  // Block / unblock confirmation
  const [statusTarget, setStatusTarget] = useState<{ company: CompanyAccount; user: MemberAccount } | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      setRows(await getCompanyAccounts());
    } catch (err: any) {
      setError(err.message || t('accounts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter(r => r.account && !r.account.disabled).length,
    disabled: rows.filter(r => r.account?.disabled).length,
    none: rows.filter(r => !r.account).length,
    emailAccounts: rows.reduce((n, r) => n + r.users.length, 0),
  }), [rows]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter === 'active' && !(r.account && !r.account.disabled)) return false;
      if (statusFilter === 'disabled' && !r.account?.disabled) return false;
      if (statusFilter === 'none' && r.account) return false;
      if (mustChangeFilter === 'yes' && !needsChange(r)) return false;
      if (mustChangeFilter === 'no' && needsChange(r)) return false;
      if (!q) return true;
      return (
        r.pcu_id?.toLowerCase().includes(q) ||
        r.name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.users.some(u => u.email.includes(q))
      );
    });
  }, [rows, searchText, statusFilter, mustChangeFilter]);

  useEffect(() => setPage(0), [searchText, statusFilter, mustChangeFilter]);

  const cityLabel = (r: CompanyAccount) => {
    const c = r.cities;
    if (!c) return null;
    const city = isRTL ? c.translation || c.city : c.city || c.translation;
    const area = isRTL ? c.areas?.translation || c.areas?.area : c.areas?.area || c.areas?.translation;
    return `${city}${area ? ` - ${area}` : ''}`;
  };

  const formatDate = (d: string | null) =>
    d
      ? new Date(d).toLocaleDateString(isRTL ? 'ar' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : t('accounts.never');

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar(t('accounts.copied'));
    } catch {
      /* clipboard blocked: the value is still visible in the field */
    }
  };

  // ---- account dialog ----
  const openDialog = (m: DialogMode) => {
    setMode(m);
    setFormEmail(m.kind === 'email' ? m.user.email : '');
    setFormPassword('');
    setDone(false);
    setDialogError('');
  };

  const dialogTitle = (m: DialogMode) => ({
    createCompany: t('accounts.createAccount'),
    addEmail: t('accounts.addEmailAccount'),
    password: t('accounts.resetPassword'),
    email: t('accounts.changeEmail'),
  })[m.kind];

  const canSave = mode
    && (!needsPassword(mode) || formPassword.length >= 6)
    && (!needsEmail(mode) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail.trim()));

  const save = async () => {
    if (!mode) return;
    setSaving(true);
    setDialogError('');
    try {
      if (mode.kind === 'createCompany') await createCompanyAccount(mode.company.id, formPassword);
      if (mode.kind === 'addEmail') await addEmailAccount(mode.company.id, formEmail, formPassword);
      if (mode.kind === 'password') await setAccountPassword(mode.user.id, formPassword);
      if (mode.kind === 'email') await setAccountEmail(mode.user.id, formEmail);
      await load();
      if (needsPassword(mode)) {
        setDone(true); // keep the dialog open so the admin can copy the password
      } else {
        setMode(null);
        setSnackbar(t('accounts.saved'));
      }
    } catch (err: any) {
      setDialogError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ---- block / unblock ----
  const saveStatus = async () => {
    if (!statusTarget) return;
    setStatusSaving(true);
    try {
      const disabled = !statusTarget.user.disabled;
      await setAccountDisabled(statusTarget.user.id, disabled);
      setSnackbar(disabled ? t('accounts.disabledSuccess') : t('accounts.enabledSuccess'));
      setStatusTarget(null);
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setStatusSaving(false);
    }
  };

  const statusChip = (a: MemberAccount | null) => {
    if (!a) return <Chip label={t('accounts.statusNone')} size="small" variant="outlined" />;
    if (a.disabled) return <Chip label={t('accounts.statusDisabled')} size="small" color="error" />;
    return <Chip label={t('accounts.statusActive')} size="small" color="success" />;
  };

  const mustChangeCell = (a: MemberAccount | null) =>
    !a ? '—' : a.must_change_password
      ? <Chip label={t('accounts.yes')} size="small" color="warning" />
      : <Chip label={t('accounts.no')} size="small" variant="outlined" />;

  // ---- one-click default password ----
  const resetToDefault = async (company: CompanyAccount, user: MemberAccount | null) => {
    try {
      const pw = defaultPassword(company.pcu_id);
      if (user) await setAccountPassword(user.id, pw);
      else await createCompanyAccount(company.id, pw);
      setSnackbar(t('accounts.defaultSet', { password: ltr(pw) }));
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ---- selection & bulk ----
  const toggleSelected = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));
  const toggleAllFiltered = () =>
    setSelected(prev => {
      const next = new Set(prev);
      filtered.forEach(r => (allFilteredSelected ? next.delete(r.id) : next.add(r.id)));
      return next;
    });

  const runBulkDefault = async () => {
    setBulkSaving(true);
    try {
      const { results } = await setDefaultPasswords([...selected]);
      setBulkResult({
        ok: results.filter(r => r.ok).length,
        created: results.filter(r => r.ok && r.created).length,
        failed: results.filter(r => !r.ok).map(r => `${r.pcu_id || '?'}: ${r.error}`),
      });
      await load();
    } catch (err: any) {
      setBulkResult({ ok: 0, created: 0, failed: [err.message] });
    } finally {
      setBulkSaving(false);
    }
  };

  /** CSV of the selected companies' logins. The password column is filled
   *  only while the account still has the default (must change) password. */
  const exportCsv = () => {
    const esc = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [[t('accounts.pcuId'), t('accounts.company'), t('accounts.login'), t('accounts.password'), t('accounts.mustChangePassword')].map(esc).join(',')];
    rows.filter(r => selected.has(r.id)).forEach(r => {
      [r.account, ...r.users].forEach(a => {
        if (!a) return;
        lines.push([
          r.pcu_id,
          r.name,
          isCompanyLogin(a.email) ? r.pcu_id : a.email,
          a.must_change_password ? defaultPassword(r.pcu_id) : '',
          a.must_change_password ? t('accounts.yes') : t('accounts.no'),
        ].map(esc).join(','));
      });
    });
    // BOM so Excel reads the Arabic correctly
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pcu-logins-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Reset / (edit email) / block buttons for one account. */
  const accountActions = (company: CompanyAccount, user: MemberAccount, editableEmail: boolean) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title={t('accounts.resetPassword')}>
        <IconButton size="small" color="primary" onClick={() => openDialog({ kind: 'password', company, user })}>
          <Key fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('accounts.resetToDefault', { password: ltr(defaultPassword(company.pcu_id)) })}>
        <IconButton size="small" color="warning" onClick={() => resetToDefault(company, user)}>
          <RestartAlt fontSize="small" />
        </IconButton>
      </Tooltip>
      {editableEmail && (
        <Tooltip title={t('accounts.changeEmail')}>
          <IconButton size="small" onClick={() => openDialog({ kind: 'email', company, user })}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={user.disabled ? t('accounts.enable') : t('accounts.disable')}>
        <IconButton size="small" color={user.disabled ? 'success' : 'error'} onClick={() => setStatusTarget({ company, user })}>
          {user.disabled ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  );

  const sorted = useMemo(() => {
    const statusRank = (r: CompanyAccount) => (!r.account ? 2 : r.account.disabled ? 1 : 0);
    const value = (r: CompanyAccount): string | number => {
      switch (sortKey) {
        case 'pcu_id': return Number(r.pcu_id) || 0;
        case 'name': return r.name || '';
        case 'city': return cityLabel(r) || '';
        case 'status': return statusRank(r);
        case 'mustChange': return needsChange(r) ? 0 : 1;
        case 'emails': return r.users.length;
        // newest sign-in across the company account and its email accounts
        case 'lastSignIn': return Math.max(0, ...[r.account, ...r.users].map(a => (a?.last_sign_in_at ? Date.parse(a.last_sign_in_at) : 0)));
      }
    };
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = value(a), vb = value(b);
      const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'ar');
      return c * dir || (Number(a.pcu_id) - Number(b.pcu_id));
    });
  }, [filtered, sortKey, sortDir, isRTL]);

  const setSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      // dates and counts are most useful newest/largest first
      setSortDir(key === 'lastSignIn' || key === 'emails' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const sortHeader = (key: SortKey, label: string) => (
    <TableCell sortDirection={sortKey === key ? sortDir : false}>
      <TableSortLabel active={sortKey === key} direction={sortKey === key ? sortDir : 'asc'} onClick={() => setSort(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const toggleCollapsed = (id: string) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const allCollapsed = rows.length > 0 && rows.every(r => r.users.length === 0 || collapsed.has(r.id));
  const toggleAllCollapsed = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(rows.filter(r => r.users.length > 0).map(r => r.id)));

  const pageRows = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
            {t('accounts.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('accounts.subtitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`${t('accounts.total')}: ${stats.total}`} size="small" color="primary" variant="outlined" />
            <Chip label={`${t('accounts.statusActive')}: ${stats.active}`} size="small" color="success" variant="outlined" />
            <Chip label={`${t('accounts.statusDisabled')}: ${stats.disabled}`} size="small" color="error" variant="outlined" />
            <Chip label={`${t('accounts.statusNone')}: ${stats.none}`} size="small" variant="outlined" />
            <Chip label={`${t('accounts.emailAccounts')}: ${stats.emailAccounts}`} size="small" variant="outlined" />
          </Box>
        </Box>
        <Tooltip title={t('accounts.refresh')}>
          <IconButton onClick={load} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <FilterBar
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={t('accounts.searchPlaceholder')}
        filters={[
          {
            name: 'status',
            label: t('accounts.companyAccountStatus'),
            options: [
              { value: 'all', label: t('accounts.all') },
              { value: 'active', label: t('accounts.statusActive') },
              { value: 'disabled', label: t('accounts.statusDisabled') },
              { value: 'none', label: t('accounts.statusNone') },
            ],
            value: statusFilter,
            onChange: (v: string) => setStatusFilter(v as StatusFilter),
            multiple: false,
          },
          {
            name: 'mustChange',
            label: t('accounts.mustChangePassword'),
            options: [
              { value: 'all', label: t('accounts.all') },
              { value: 'yes', label: t('accounts.yes') },
              { value: 'no', label: t('accounts.no') },
            ],
            value: mustChangeFilter,
            onChange: (v: string) => setMustChangeFilter(v as MustChangeFilter),
            multiple: false,
          },
        ]}
      />

      {selected.size > 0 && (
        <Paper sx={{ mb: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', bgcolor: 'action.selected' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('accounts.selectedCount', { count: selected.size })}
          </Typography>
          <Button size="small" variant="contained" color="warning" startIcon={<RestartAlt />} onClick={() => { setBulkResult(null); setBulkOpen(true); }}>
            {t('accounts.bulkDefault')}
          </Button>
          <Button size="small" variant="outlined" startIcon={<Download />} onClick={exportCsv}>
            {t('accounts.exportCsv')}
          </Button>
          <Button size="small" onClick={() => setSelected(new Set())}>{t('accounts.clearSelection')}</Button>
        </Paper>
      )}

      <Paper sx={{ mb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Tooltip title={t('accounts.selectAllFiltered', { count: filtered.length })}>
                        <Checkbox
                          size="small"
                          checked={allFilteredSelected}
                          indeterminate={!allFilteredSelected && filtered.some(r => selected.has(r.id))}
                          onChange={toggleAllFiltered}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell padding="checkbox">
                      <Tooltip title={allCollapsed ? t('accounts.expandAll') : t('accounts.collapseAll')}>
                        <IconButton size="small" onClick={toggleAllCollapsed}>
                          {allCollapsed ? <UnfoldMore fontSize="small" /> : <UnfoldLess fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    {sortHeader('pcu_id', t('accounts.pcuId'))}
                    {sortHeader('name', t('accounts.company'))}
                    {sortHeader('city', t('accounts.city'))}
                    {sortHeader('status', t('accounts.companyAccountStatus'))}
                    {sortHeader('mustChange', t('accounts.mustChangePassword'))}
                    {sortHeader('emails', t('accounts.emailAccounts'))}
                    {sortHeader('lastSignIn', t('accounts.lastSignIn'))}
                    <TableCell>{t('accounts.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>{t('accounts.noResults')}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {pageRows.map(r => {
                    const open = !collapsed.has(r.id);
                    return (
                      <React.Fragment key={r.id}>
                        <TableRow hover selected={selected.has(r.id)} sx={{ bgcolor: 'background.paper' }}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={selected.has(r.id)} onChange={() => toggleSelected(r.id)} />
                          </TableCell>
                          <TableCell padding="checkbox">
                            {r.users.length > 0 && (
                              <IconButton size="small" onClick={() => toggleCollapsed(r.id)}>
                                {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{r.pcu_id}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                          <TableCell>
                            {cityLabel(r) ? <Chip label={cityLabel(r)} size="small" variant="outlined" /> : '—'}
                          </TableCell>
                          <TableCell>{statusChip(r.account)}</TableCell>
                          <TableCell>{mustChangeCell(r.account)}</TableCell>
                          <TableCell>
                            <Chip label={r.users.length} size="small" onClick={r.users.length ? () => toggleCollapsed(r.id) : undefined} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{r.account ? formatDate(r.account.last_sign_in_at) : '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {r.account ? (
                                accountActions(r, r.account, false)
                              ) : (
                                <>
                                  <Tooltip title={t('accounts.createAccount')}>
                                    <IconButton size="small" color="primary" onClick={() => openDialog({ kind: 'createCompany', company: r })}>
                                      <Business fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('accounts.createWithDefault', { password: ltr(defaultPassword(r.pcu_id)) })}>
                                    <IconButton size="small" color="warning" onClick={() => resetToDefault(r, null)}>
                                      <RestartAlt fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              <Tooltip title={t('accounts.addEmailAccount')}>
                                <IconButton size="small" onClick={() => openDialog({ kind: 'addEmail', company: r })}>
                                  <PersonAdd fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                        {open && r.users.map(u => (
                          <TableRow
                            key={u.id}
                            hover
                            sx={{
                              bgcolor: 'action.hover',
                              '& > td': { py: 0.25, fontSize: '0.8rem', borderBottomStyle: 'dashed' },
                            }}
                          >
                            <TableCell padding="checkbox" />
                            <TableCell padding="checkbox" />
                            <TableCell sx={{ color: 'text.disabled' }}>↳</TableCell>
                            <TableCell colSpan={2} dir="ltr" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                                {u.email}
                              </Box>
                            </TableCell>
                            <TableCell>{statusChip(u)}</TableCell>
                            <TableCell>{mustChangeCell(u)}</TableCell>
                            <TableCell />
                            <TableCell><Typography variant="caption">{formatDate(u.last_sign_in_at)}</Typography></TableCell>
                            <TableCell>{accountActions(r, u, true)}</TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Create / add / reset password / change email */}
      <Dialog open={!!mode} onClose={() => !saving && setMode(null)} maxWidth="xs" fullWidth>
        {mode && (
          <>
            <DialogTitle>{dialogTitle(mode)}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                {mode.company.name} — {t('accounts.pcuId')}: <strong>{mode.company.pcu_id}</strong>
                {(mode.kind === 'password' || mode.kind === 'email') && (
                  <Box component="span" dir="ltr" sx={{ display: 'block' }}>
                    {mode.user.email.endsWith('@members.pcu.ps') ? t('accounts.companyAccount') : mode.user.email}
                  </Box>
                )}
              </DialogContentText>
              {mode.kind === 'createCompany' && (
                <Alert severity="info" sx={{ mb: 2 }}>{t('accounts.companyAccountHint', { pcuId: mode.company.pcu_id })}</Alert>
              )}
              {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
              {done && <Alert severity="success" sx={{ mb: 2 }}>{t('accounts.passwordSaved')}</Alert>}
              {needsEmail(mode) && (
                <TextField
                  autoFocus
                  fullWidth
                  type="email"
                  label={t('accounts.email')}
                  value={formEmail}
                  onChange={e => setFormEmail(e.target.value)}
                  disabled={saving || done}
                  sx={{ mb: 2 }}
                  inputProps={{ dir: 'ltr' }}
                />
              )}
              {needsPassword(mode) && (
                <TextField
                  autoFocus={!needsEmail(mode)}
                  fullWidth
                  label={t('accounts.password')}
                  value={formPassword}
                  onChange={e => setFormPassword(e.target.value)}
                  disabled={saving || done}
                  helperText={done ? t('accounts.shareHint') : t('accounts.passwordHint')}
                  inputProps={{ dir: 'ltr', style: { fontFamily: 'monospace' } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {!done && (
                          <Tooltip title={t('accounts.generate')}>
                            <IconButton onClick={() => setFormPassword(generatePassword())} edge="end" disabled={saving}>
                              <Casino />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('accounts.copy')}>
                          <span>
                            <IconButton onClick={() => copy(formPassword)} edge="end" disabled={!formPassword}>
                              <ContentCopy />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </DialogContent>
            <DialogActions>
              {done ? (
                <Button variant="contained" onClick={() => setMode(null)}>{t('accounts.done')}</Button>
              ) : (
                <>
                  <Button onClick={() => setMode(null)} disabled={saving}>{t('accounts.cancel')}</Button>
                  <Button
                    variant="contained"
                    onClick={save}
                    disabled={saving || !canSave}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    {t('accounts.save')}
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Bulk: default password */}
      <Dialog open={bulkOpen} onClose={() => !bulkSaving && setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('accounts.bulkDefault')}</DialogTitle>
        <DialogContent>
          {bulkResult ? (
            <>
              <Alert severity={bulkResult.failed.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
                {t('accounts.bulkDone', { ok: bulkResult.ok, created: bulkResult.created })}
              </Alert>
              {bulkResult.failed.length > 0 && (
                <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                  {t('accounts.bulkFailed', { count: bulkResult.failed.length })}
                  {'\n' + bulkResult.failed.slice(0, 10).join('\n')}
                </Alert>
              )}
            </>
          ) : (
            <DialogContentText>
              {t('accounts.bulkConfirm', { count: selected.size, example: ltr(defaultPassword('1215')) })}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {bulkResult ? (
            <>
              <Button startIcon={<Download />} onClick={exportCsv}>{t('accounts.exportCsv')}</Button>
              <Button variant="contained" onClick={() => setBulkOpen(false)}>{t('accounts.done')}</Button>
            </>
          ) : (
            <>
              <Button onClick={() => setBulkOpen(false)} disabled={bulkSaving}>{t('accounts.cancel')}</Button>
              <Button
                variant="contained"
                color="warning"
                onClick={runBulkDefault}
                disabled={bulkSaving}
                startIcon={bulkSaving ? <CircularProgress size={16} color="inherit" /> : <RestartAlt />}
              >
                {bulkSaving ? t('accounts.working') : t('accounts.apply')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Block / unblock */}
      <Dialog open={!!statusTarget} onClose={() => !statusSaving && setStatusTarget(null)}>
        <DialogTitle>
          {statusTarget?.user.disabled ? t('accounts.enable') : t('accounts.disable')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(() => {
              if (!statusTarget) return null;
              const who = statusTarget.user.email.endsWith('@members.pcu.ps')
                ? `${statusTarget.company.name} (${statusTarget.company.pcu_id})`
                : statusTarget.user.email;
              return statusTarget.user.disabled
                ? t('accounts.enableConfirm', { name: who })
                : t('accounts.disableConfirm', { name: who });
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusTarget(null)} disabled={statusSaving}>{t('accounts.cancel')}</Button>
          <Button
            variant="contained"
            color={statusTarget?.user.disabled ? 'success' : 'error'}
            onClick={saveStatus}
            disabled={statusSaving}
          >
            {statusTarget?.user.disabled ? t('accounts.enable') : t('accounts.disable')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar('')} message={snackbar} />
    </Box>
  );
};

export default AuthSystemPage;
